import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { BehaviorSubject, Observable, ReplaySubject, Subject, Subscription } from "rxjs";
import { environment } from "src/environments/environment";
import { NavigationEnd, Router } from '@angular/router';

import { AuthData, AuthResponseData, OtpSendResponse, OtpVerifyResponse } from "./auth-data.model";
import { DialogService } from '../dialog/dialog.service';
import { DataSharingService } from '../main-section/data-shering-service/data-sharing.service';
import { DirectionService } from '../direction.service'
import { filter, tap } from 'rxjs/operators';
import { UsersService } from '../super-management/user/users.service';

const BACKEND_URL = environment.apiUrl + "/user";

@Injectable({ providedIn: "root" })

export class AuthService {
  /** כוונת אורח: בחר מוצר PH לפני login — לא מסתמך רק על localStorage */
  private static readonly PH_GUEST_PLACE_INTENT_KEY = 'phGuestPlaceIntent';

  /** אחרי login מכפתור צ'אט בבחירת סניף/מוצר — ניווט ל־/chat/.../system/... בלי RightPlace */
  private static readonly POST_LOGIN_SYSTEM_CHAT_KEY = 'postLoginSystemChat';

  /** טלפון שאומת ב-SMS בהרשמה, כשהמייל כבר שייך לחשבון — מצורף לחשבון אחרי שהמשתמש מתחבר אליו */
  private static readonly PENDING_PHONE_TOKEN_KEY = 'pendingPhoneToken';

  /** מסכי עמדה / מוניטור — לא חוסמים אותם בדיאלוג החובה להוספת טלפון */
  private static readonly PHONE_GATE_EXEMPT_PATHS = ['/screen', '/monitor', '/express-internal-queue', '/queue/', '/r/'];

  /** המקום (שירות/סניף) שנשלח עם אימות קוד ה-SMS — משמש לסיום ההתחברות / ההרשמה בטלפון */
  private phoneLoginPlace = { printingService: '', branch: '' };
  private phoneSignupPlace = { printingService: '', branch: '' };

  private isAuthenticated = false;
  private token: string;
  private tokenTimer: any;
  private userId: string;
  private authStatusListener = new Subject<boolean>();
  private branchSubscription: Subscription;
  private printServiceSubscription: Subscription;
  printingService: string = '';
  branch: string = '';
  private rolesSubject = new ReplaySubject<string[]>(1);
  public roles$ = this.rolesSubject.asObservable();
  private userNameSubject = new BehaviorSubject<string>('');
  public userName$ = this.userNameSubject.asObservable();
  private emailSubject = new BehaviorSubject<string>('');
  public email$ = this.emailSubject.asObservable();

  selectedLanguage: string = 'he';
  private authCompleted = new Subject<void>();
  isRightPlaceOpened: boolean = false;

  private user: any;
  private userUpdated = new Subject<any>();
  userUpdated$ = this.userUpdated.asObservable();

  constructor(
    private http: HttpClient,
    private dialogService: DialogService,
    private dataSharingService: DataSharingService,
    private router: Router,
    private directionService: DirectionService,
    private usersService: UsersService,
    // public socialService: SocialAuthService
  ) {
    this.branchSubscription = this.dataSharingService.getBranch().subscribe((value) => {
      this.branch = value;
    });

    this.printServiceSubscription = this.dataSharingService.getPrintingService().subscribe((value) => {
      this.printingService = value;
    });

    this.directionService.currentLanguage$.subscribe(lang => {
      this.selectedLanguage = lang;
      // console.log("selected---Lang---uage: 1" + this.selectedLanguage)
    });
  }

  getToken() {
    return this.token;
  }

  getIsAuth() {
    // console.log("isAuthenticated: " + this.isAuthenticated)
    return this.isAuthenticated;
  }

  getUserId() {
    return this.userId;
  }

  getEmail() {
    // First try to get from localStorage
    const email = localStorage.getItem('email');
    // Check that email is valid (not null, not "undefined" string, not empty)
    if (email && email !== 'undefined' && email.trim() !== '') {
      return email;
    }
    // Fallback to BehaviorSubject value (might be empty if not initialized)
    const subjectEmail = this.emailSubject.value;
    if (subjectEmail && subjectEmail !== 'undefined' && subjectEmail.trim() !== '') {
      return subjectEmail;
    }
    return '';
  }

  /**
   * New users may return empty `home_*` lists. In that case, fall back to the place the client
   * intended to save (usually already present on `authData` from localStorage).
   */
  private resolveHomePlaceForAuthSave(
    homeServices: string[] | undefined,
    homeBranches: string[] | undefined,
    authData: Pick<AuthData, 'printingService' | 'branch'>
  ): { printingService: string; branch: string } {
    const svc0 = homeServices && homeServices.length > 0 ? homeServices[0] : '';
    const br0 = homeBranches && homeBranches.length > 0 ? homeBranches[0] : '';
    const fallbackSvc = (authData?.printingService || '').trim();
    const fallbackBr = (authData?.branch || '').trim();
    return {
      printingService: svc0 && svc0 !== '' ? svc0 : fallbackSvc,
      branch: br0 && br0 !== '' ? br0 : fallbackBr,
    };
  }

  private readPhGuestPlaceIntent(): { printingService: string; branch: string } | null {
    try {
      const raw = sessionStorage.getItem(AuthService.PH_GUEST_PLACE_INTENT_KEY);
      if (!raw) {
        return null;
      }
      const v = JSON.parse(raw);
      const exp = Number(v?.exp) || 0;
      if (exp && Date.now() > exp) {
        sessionStorage.removeItem(AuthService.PH_GUEST_PLACE_INTENT_KEY);
        return null;
      }
      if (v?.printingService === 'ph' && v?.branch && String(v.branch).trim() !== '') {
        return { printingService: 'ph', branch: String(v.branch).trim() };
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  /** המקום שהמשתמש בחר לפני ההתחברות: כוונת אורח PH, ואחריה השירות/סניף השמורים ב-localStorage. */
  private readLoginPlaceIntent(): { printingService: string; branch: string } {
    const phGuestIntent = this.readPhGuestPlaceIntent();
    if (phGuestIntent?.printingService === 'ph' && phGuestIntent.branch) {
      return phGuestIntent;
    }
    const printingService = localStorage.getItem("printingService");
    const branch = localStorage.getItem("branch");
    if (printingService && printingService !== '' && branch && branch !== '') {
      return { printingService, branch };
    }
    return { printingService: '', branch: '' };
  }

  private clearPhGuestPlaceIntent(): void {
    try {
      sessionStorage.removeItem(AuthService.PH_GUEST_PLACE_INTENT_KEY);
    } catch {
      /* ignore */
    }
  }

  /**
   * מנקה כוונת צ'אט מערכת אחרי-login (למשל ב־logout).
   */
  clearPostLoginSystemChatIntent(): void {
    try {
      localStorage.removeItem(AuthService.POST_LOGIN_SYSTEM_CHAT_KEY);
    } catch {
      /* ignore */
    }
  }

  /**
   * אם נשמרה כוונת צ'אט (מ־DialogService.onOpenLoginDialog) — מנווט לצ'אט המערכת ומחזיר true.
   * @param finalize נקרא אחרי שהניווט הושלם (למשל triggerAuthComplete + updateUser).
   */
  private consumePostLoginSystemChatNavigation(
    userId: string | null | undefined,
    finalize: () => void
  ): boolean {
    if (!userId || userId === '' || userId === 'undefined' || userId === 'null') {
      return false;
    }
    let system: '3DPrint' | 'phPrint' | null = null;
    try {
      const raw = localStorage.getItem(AuthService.POST_LOGIN_SYSTEM_CHAT_KEY);
      if (raw === '3DPrint' || raw === 'phPrint') {
        system = raw;
      }
    } catch {
      return false;
    }
    if (!system) {
      return false;
    }
    try {
      localStorage.removeItem(AuthService.POST_LOGIN_SYSTEM_CHAT_KEY);
    } catch {
      /* ignore */
    }
    void this.router.navigate(['/chat', 'user', userId, 'system', system]).then(() => finalize());
    return true;
  }

  /**
   * אחרי OAuth (SocialComponent): ניווט לצ'אט מערכת אם נשמרה כוונה מדיאלוג ההתחברות.
   * `autoAuthUser` כבר קורא ל־triggerAuthComplete; כאן מסיימים עם updateUser אחרי הניווט.
   */
  navigatePostLoginSystemChatAfterSocialIfNeeded(userId: string): boolean {
    return this.consumePostLoginSystemChatNavigation(userId, () => {
      this.updateUser(null);
    });
  }

  getAuthStatusListener() {
    return this.authStatusListener.asObservable();
  }

  createUser(email: string, password: string, provider: string = 'local') {
    const place = this.readLoginPlaceIntent();
    if (place.printingService) {
      this.printingService = place.printingService;
      this.branch = place.branch;
    }
    const authData: AuthData = {
      email: email,
      password: password,
      printingService: this.printingService,
      branch: this.branch,
      provider: provider,
      language: this.selectedLanguage
    };
    this.http
      .post<AuthResponseData>(BACKEND_URL + "/signup", authData).subscribe({
        next: (response) => {
          this.dialogService.onCloseLoginDialog();
          this.completeSignup(response, authData);
        },
        error: (error) => {
          // If user was created but there's an error (e.g., 500), check if we have token in response
          // Sometimes backend returns error but still creates the user
          if (error.error && error.error.token) {
            // User was created despite the error - handle as success
            this.completeSignup(error.error, authData);
            return; // Don't show error if user was created successfully
          }
          // If no token in error response, show error
          console.error('Signup error:', error);
          this.authStatusListener.next(false);
        }
      });
  }

  /** Saves the session of a new account (email or phone sign-up) and takes the user to the place they picked. */
  private completeSignup(response: AuthResponseData, place: Pick<AuthData, 'printingService' | 'branch'>) {
    const token = response.token;
    this.token = token;
    if (token) {
      const expiresInDuration = response.expiresIn;
      this.setAuthTimer(expiresInDuration);
      this.isAuthenticated = true;
      this.userId = response.userId;
      const resolved = this.resolveHomePlaceForAuthSave(
        response.home_printingServices_list,
        response.home_branches_list,
        place
      );
      this.printingService = resolved.printingService;
      this.branch = resolved.branch;
      this.authStatusListener.next(true);
      const now = new Date();
      const expirationDate = new Date(now.getTime() + expiresInDuration * 1000);
      this.rolesSubject.next(response.roles || []);
      this.saveAuthData(
        token,
        expirationDate,
        response.userId,
        resolved.printingService,
        resolved.branch,
        response.language,
        response.roles || [],
        response.userName,
        response.email,
      );
      this.dialogService.onCloseLoginDialog();
    }
    const finalizeSignup = () => {
      this.triggerAuthComplete();
      this.updateUser(null);
    };
    if (token && this.consumePostLoginSystemChatNavigation(response.userId, finalizeSignup)) {
      return;
    }
    if (this.printingService === 'ph' && this.branch && this.branch !== '') {
      this.clearPhGuestPlaceIntent();
      this.dataSharingService.setPrintingService('ph');
      this.dataSharingService.setBranch(this.branch);
      this.dataSharingService.setProduct(this.branch);
      this.updateAuthData('ph', this.branch);
      this.usersService.updateUserPlace('ph', this.branch).subscribe({
        next: () => {
          this.router.navigate(['/phprint']);
          finalizeSignup();
        },
        error: () => {
          this.router.navigate(['/phprint']);
          finalizeSignup();
        },
      });
    } else if (this.printingService && this.printingService !== '' && this.branch && this.branch !== '') {
      this.dataSharingService.setPrintingService(this.printingService);
      this.dataSharingService.setBranch(this.branch);
      this.router.navigate(["/print"]);
      finalizeSignup();
    } else {
      finalizeSignup();
    }
  }

  checkEmail(email: string): Observable<any> {
    return this.http.post<boolean>(BACKEND_URL + "/checkemail", { email: email });
  }

  facebookLogin() {
    this.setIsFromSocial();
    this.http.get(BACKEND_URL + "/auth/facebook").subscribe(response => {
      window.location.href = response['url'];
      this.triggerAuthComplete();
    })
  }

  googleLogin() {
    this.setIsFromSocial();
    this.http.get(BACKEND_URL + "/auth/google").subscribe(response => {
      window.location.href = response['url'];
      this.triggerAuthComplete();
    })
  }

  setIsFromSocial() {
    const service = localStorage.getItem("printingService");
    const branch = localStorage.getItem("branch");
    if (service && service !== '' && branch && branch !== '') {
      localStorage.setItem("isfromSocial", "true");
    } else {
      localStorage.setItem("isfromSocial", "false");
    }
  }

  login(email: string, password: string, provider: string = 'local') {
    const { printingService: newPrintingService, branch: newBranch } = this.readLoginPlaceIntent();

    const authData: AuthData = {
      email: email,
      password: password,
      printingService: newPrintingService,
      branch: newBranch,
      provider: provider,
      language: '',
    };
    this.http
      .post<AuthResponseData>(BACKEND_URL + "/login", authData)
      .subscribe(response => {
        this.completeLogin(response, newPrintingService, newBranch);
      }, error => {
        this.authStatusListener.next(false);
      });
  }

  /** Saves the session of an existing account (password or phone login) and takes the user to the place they picked. */
  private completeLogin(response: AuthResponseData, newPrintingService: string, newBranch: string) {
    const token = response.token;
    this.token = token;
    if (token) {
      const expiresInDuration = response.expiresIn;
      this.setAuthTimer(expiresInDuration);
      this.isAuthenticated = true;
      this.userId = response.userId;

      this.printingService = response.home_printingServices_list?.[0] ?? '';
      this.branch = response.home_branches_list?.[0] ?? '';

      if (newPrintingService === 'ph' && newBranch) {
        this.printingService = 'ph';
        this.branch = newBranch;
      } else if (
        localStorage.getItem("printingService") &&
        localStorage.getItem("printingService") !== '' &&
        localStorage.getItem("branch") &&
        localStorage.getItem("branch") !== ''
      ) {
        this.printingService = localStorage.getItem("printingService")!;
        this.branch = localStorage.getItem("branch")!;
      }

      this.authStatusListener.next(true);
      const now = new Date();
      const expirationDate = new Date(now.getTime() + expiresInDuration * 1000);
      this.rolesSubject.next(response.roles);
      this.saveAuthData(
        token,
        expirationDate,
        response.userId,
        this.printingService,
        this.branch,
        response.language,
        response.roles,
        response.userName,
        response.email,
      );
      if (response.language && response.language !== '') {
        this.directionService.toLanguageDirection(response.language);
      }
      this.dialogService.onCloseLoginDialog();

      const finalizeLogin = () => {
        this.triggerAuthComplete();
        this.updateUser(null);
      };

      if (this.consumePostLoginSystemChatNavigation(this.userId, finalizeLogin)) {
        return;
      }

      if (newPrintingService === 'ph' && newBranch) {
        this.clearPhGuestPlaceIntent();
        this.dataSharingService.setPrintingService('ph');
        this.dataSharingService.setBranch(newBranch);
        this.dataSharingService.setProduct(newBranch);
        this.updateAuthData('ph', newBranch);
        this.usersService.updateUserPlace('ph', newBranch).subscribe({
          next: () => {
            this.router.navigate(['/phprint']);
            finalizeLogin();
          },
          error: () => {
            this.router.navigate(['/phprint']);
            finalizeLogin();
          },
        });
        return;
      }

      if (newPrintingService && newBranch) {
        this.dataSharingService.setPrintingService(newPrintingService);
        this.dataSharingService.setBranch(newBranch);
        if (newPrintingService === 'ph') {
          this.dataSharingService.setProduct(newBranch);
          this.router.navigate(["/phprint"]);
        } else {
          this.router.navigate(["/print"]);
        }
        finalizeLogin();
        return;
      }

      if (this.printingService && this.printingService !== '' && this.branch && this.branch !== '') {
        this.dataSharingService.setPrintingService(this.printingService);
        this.dataSharingService.setBranch(this.branch);
        if (!localStorage.getItem("dontOpenRP")) {
          this.dialogService.onOpenRightPlaceDialog();
        } else {
          localStorage.removeItem("dontOpenRP");
        }
      }
      finalizeLogin();
    }
  }

  // ===== Phone (SMS code) login =====

  sendLoginCode(phone: string): Observable<OtpSendResponse> {
    return this.http.post<OtpSendResponse>(BACKEND_URL + "/otp/send", {
      phone: phone,
      language: this.selectedLanguage,
    });
  }

  /** LOGGED_IN finishes the login here; the other statuses are for the login dialog to handle. */
  verifyLoginCode(phone: string, code: string): Observable<OtpVerifyResponse> {
    this.phoneLoginPlace = this.readLoginPlaceIntent();
    return this.http
      .post<OtpVerifyResponse>(BACKEND_URL + "/otp/verify", { phone, code, ...this.phoneLoginPlace })
      .pipe(tap(response => this.completePhoneLoginIfLoggedIn(response)));
  }

  /** Log in to one of the accounts that share the verified phone. */
  selectPhoneAccount(phoneToken: string, userId: string): Observable<OtpVerifyResponse> {
    return this.http
      .post<OtpVerifyResponse>(BACKEND_URL + "/otp/select", { phoneToken, userId, ...this.phoneLoginPlace })
      .pipe(tap(response => this.completePhoneLoginIfLoggedIn(response)));
  }

  registerWithPhone(phoneToken: string, firstName: string, lastName: string, email: string): Observable<OtpVerifyResponse> {
    const intent = this.readLoginPlaceIntent();
    this.phoneSignupPlace = intent.printingService ? intent : { printingService: this.printingService, branch: this.branch };
    return this.http
      .post<OtpVerifyResponse>(BACKEND_URL + "/otp/register", {
        phoneToken,
        firstName,
        lastName,
        email,
        language: this.selectedLanguage,
        ...this.phoneSignupPlace,
      })
      .pipe(tap(response => {
        if (response?.status === 'LOGGED_IN') {
          this.completeSignup(response, this.phoneSignupPlace);
        }
      }));
  }

  private completePhoneLoginIfLoggedIn(response: OtpVerifyResponse | null) {
    if (response?.status === 'LOGGED_IN') {
      this.completeLogin(response, this.phoneLoginPlace.printingService, this.phoneLoginPlace.branch);
    }
  }

  /**
   * The phone was verified but its email already belongs to an account: keep the proof so the phone
   * is attached right after the user logs in to that account (password / Google / Facebook redirect).
   */
  rememberVerifiedPhoneForNextLogin(phoneToken: string): void {
    try {
      sessionStorage.setItem(AuthService.PENDING_PHONE_TOKEN_KEY, phoneToken);
    } catch {
      /* ignore */
    }
  }

  clearPendingVerifiedPhone(): void {
    try {
      sessionStorage.removeItem(AuthService.PENDING_PHONE_TOKEN_KEY);
    } catch {
      /* ignore */
    }
  }

  private takePendingVerifiedPhone(): string | null {
    try {
      const phoneToken = sessionStorage.getItem(AuthService.PENDING_PHONE_TOKEN_KEY);
      sessionStorage.removeItem(AuthService.PENDING_PHONE_TOKEN_KEY);
      return phoneToken;
    } catch {
      return null;
    }
  }

  // ===== Phone for logged-in accounts =====

  sendPhoneVerificationCode(phone: string): Observable<OtpSendResponse> {
    return this.http.post<OtpSendResponse>(BACKEND_URL + "/phone/send", {
      phone: phone,
      language: this.selectedLanguage,
    });
  }

  verifyPhoneCode(phone: string, code: string): Observable<{ message: string; phone: number }> {
    return this.http.post<{ message: string; phone: number }>(BACKEND_URL + "/phone/verify", { phone, code });
  }

  /**
   * Accounts without a phone must add and verify one (a number no other account uses) before going on;
   * accounts whose unverified number is also on another account must confirm it (or enter another).
   */
  private enforcePhoneGate(): void {
    if (!this.isAuthenticated || !this.token) {
      return;
    }
    const pendingPhoneToken = this.takePendingVerifiedPhone();
    if (pendingPhoneToken) {
      this.http.post<{ phone: number }>(BACKEND_URL + "/phone/attach", { phoneToken: pendingPhoneToken }).subscribe({
        next: (response) => {
          if (response?.phone) {
            this.usersService.updatePhone(response.phone);
          } else {
            this.openPhoneGateIfNeeded();
          }
        },
        error: () => this.openPhoneGateIfNeeded(),
      });
      return;
    }
    this.openPhoneGateIfNeeded();
  }

  private openPhoneGateIfNeeded(): void {
    // station screens / monitors can run under a staff session — never cover them with a blocking dialog
    const path = window.location.pathname;
    if (AuthService.PHONE_GATE_EXEMPT_PATHS.some(exempt => path.startsWith(exempt))) {
      return;
    }
    this.http.get<{ hasPhone: boolean; phoneShared: boolean; phone: number | null }>(BACKEND_URL + "/phone/status").subscribe({
      next: (response) => {
        if (!response || !this.isAuthenticated) {
          return;
        }
        if (!response.hasPhone) {
          this.dialogService.onOpenPhoneDialog(true);
        } else if (response.phoneShared) {
          // the number is also on another account: confirm it by SMS, or enter another one
          this.dialogService.onOpenPhoneDialog(true, response.phone);
        }
      },
      error: () => {
        /* checked again on the next login / app load */
      },
    });
  }

  triggerAuthComplete() {
    this.refreshHaveWallFlag();
    this.enforcePhoneGate();
    this.authCompleted.next();
  }

  /**
   * After login / session restore: set localStorage.haveWall=true if user has a wall.
   * Cleared on logout via clearAuthData.
   */
  refreshHaveWallFlag(): void {
    if (!this.isAuthenticated || !this.token) {
      localStorage.removeItem('haveWall');
      return;
    }
    this.http
      .get<{ message?: string; wall: { _id?: string } | null }>(
        environment.apiUrl + '/wall/me'
      )
      .subscribe({
        next: (res) => {
          if (res?.wall?._id) {
            localStorage.setItem('haveWall', 'true');
          } else {
            localStorage.removeItem('haveWall');
          }
        },
        error: () => {
          /* keep previous value on transient errors */
        },
      });
  }

  /** Call after first publish (or when wall is known to exist). */
  setHaveWall(hasWall: boolean): void {
    if (hasWall) {
      localStorage.setItem('haveWall', 'true');
    } else {
      localStorage.removeItem('haveWall');
    }
  }

  autoAuthUser() {
    const authInformation = this.getAuthData();
    if (!authInformation) {
      return;
    }
    const now = new Date();
    const expiresIn = authInformation.expirationDate.getTime() - now.getTime();
    if (expiresIn > 0) {
      this.token = authInformation.token;
      this.isAuthenticated = true;
      this.userId = authInformation.userId;
      this.setAuthTimer(expiresIn / 1000);
      this.authStatusListener.next(true);
      this.rolesSubject.next(authInformation.roles);
      if (authInformation.language || authInformation.language === '') {
        this.directionService.toLanguageDirection(authInformation.language);
        // console.log("selected---Lang---uage: 2" + authInformation.language);
        // setTimeout(() => {
        //     this.directionService.toLanguageDirection(authInformation.language);
        //     // console.log("selected---Lang---uage: 8" + authInformation.language);
        // }, 3000);
      }
      if (authInformation.printingService && authInformation.printingService !== '' &&
        authInformation.branch && authInformation.branch !== '') {
        this.router.events.pipe(
          filter(event => event instanceof NavigationEnd)
        ).subscribe((event: NavigationEnd) => {
          const isfromSocial = localStorage.getItem("isfromSocial");
          if ((event.urlAfterRedirects === '/' ||
            event.urlAfterRedirects === '/branch' ||
            event.urlAfterRedirects === '/product' ||
            event.urlAfterRedirects === '/3dproduct' ||
            event.urlAfterRedirects === '/phproduct') &&
            !this.isRightPlaceOpened) {
            if (authInformation.printingService && authInformation.printingService !== '' &&
              authInformation.branch && authInformation.branch !== '') {
              // console.log("autoAuthUser: ", authInformation.printingService, authInformation.branch);
              this.dataSharingService.setPrintingService(authInformation.printingService);
              this.dataSharingService.setBranch(authInformation.branch);
              if (authInformation.printingService === 'ph') {
                this.dataSharingService.setProduct(authInformation.branch);
              }
              this.dialogService.onOpenRightPlaceDialog();
            }
          }
          this.isRightPlaceOpened = true;
          if (isfromSocial && isfromSocial !== '') {
            this.printingService = localStorage.getItem("printingService");
            this.branch = localStorage.getItem("branch");
            if (this.printingService && this.printingService !== '' && this.printingService !== 'null'
              && this.branch && this.branch !== '' && this.branch !== 'null') {
              this.usersService.updateUserPlaceAsync(this.printingService, this.branch).subscribe(() => {
                this.dataSharingService.setPrintingService(this.printingService);
                this.dataSharingService.setBranch(this.branch);
                if (this.printingService === 'ph') {
                  this.dataSharingService.setProduct(this.branch);
                }
              });
            }
            localStorage.removeItem("isfromSocial");
          }
        });
      }
      this.triggerAuthComplete();
    }
    this.userNameSubject.next(authInformation.userName);
    this.emailSubject.next(authInformation.email);
  }

  getAuthCompletedListener() {
    return this.authCompleted.asObservable();
  }

  /** עמודים ציבוריים – כמו qanda, נגישים לכל משתמש; כשקוראים ל־logout כשהמשתמש בהם, לא מפנים ל־/ */
  private static readonly PUBLIC_ROUTES = ['/edit', '/qanda', '/home', '/tandc', '/pp', '/joinus', '/helpers', '/support', '/remove', '/r'];

  logout(isRediredtToMain = true): Promise<void> {
    return new Promise((resolve) => {
      this.token = null;
      this.isAuthenticated = false;
      this.authStatusListener.next(false);
      this.userId = null;
      this.printingService = '';
      this.branch = '';
      clearTimeout(this.tokenTimer);
      this.clearAuthData();
      this.dataSharingService.setPrintingService('');
      this.dataSharingService.setBranch('');
      this.dataSharingService.setCluster('');
      this.rolesSubject.next([]);
      this.userNameSubject.next('');
      this.user = null;
      this.userUpdated.next(this.user);
      this.updateUser(null);
      if (isRediredtToMain) {
        const url = this.router.url.split('?')[0];
        const isOnPublicRoute = AuthService.PUBLIC_ROUTES.some(
          (r) => url === r || url.startsWith(r + '/')
        );
        if (!isOnPublicRoute) {
          const isAlreadyOnRoot = url === '/' || url === '';
          if (isAlreadyOnRoot) {
            resolve();
          } else {
            this.router.navigate(["/"]).then(() => resolve());
          }
        } else {
          resolve();
        }
      } else {
        resolve();
      }
    });
  }

  private setAuthTimer(duration: number) {
    // console.log("setting timer: " + duration)
    this.tokenTimer = setTimeout(() => {
      this.logout();
      this.dialogService.onCloseRightPlaceDialog();
      this.dialogService.onOpenLoginDialog('', '');
    }, duration * 1000)
  }

  saveAuthData(
    token: string,
    expirationDate: Date,
    userId: string,
    printingService: string,
    branch: string,
    language: string,
    roles: string[],
    userName: string,
    email: string,
  ) {
    localStorage.setItem("token", token);
    localStorage.setItem("expiration", expirationDate.toISOString());
    localStorage.setItem("userId", userId);
    localStorage.setItem("printingService", printingService);
    localStorage.setItem("branch", branch);
    localStorage.setItem("language", language);
    localStorage.setItem("roles", JSON.stringify(roles));
    localStorage.setItem("userName", userName);
    // Only save email if it's a valid string (not undefined, null, or empty)
    if (email && typeof email === 'string' && email.trim() !== '') {
      localStorage.setItem("email", email);
    }

    this.userNameSubject.next(userName);
  }

  private clearAuthData() {
    this.clearPhGuestPlaceIntent();
    localStorage.removeItem("token");
    localStorage.removeItem("expiration");
    localStorage.removeItem("userId");
    localStorage.removeItem("printingService");
    localStorage.removeItem("branch");
    localStorage.removeItem("roles");
    localStorage.removeItem("userName");
    localStorage.removeItem("email");
    localStorage.removeItem("isfromSocial");
    localStorage.removeItem("lastPrintingService");
    localStorage.removeItem("lastBranch");
    localStorage.removeItem("isNotAcademy");
    localStorage.removeItem("wallStatus");
    localStorage.removeItem("haveWall");
    localStorage.removeItem("nopfa");
    localStorage.removeItem("redirectTo");
    localStorage.removeItem("searchedBranchId");
    localStorage.removeItem("searchedService");
    localStorage.removeItem("hideLangModel");
    localStorage.removeItem("isDpiUpdated");
    // Clear monitor authentication when user logs out
    localStorage.removeItem("monitorAuthenticated");
    localStorage.removeItem("monitorAuthTimestamp");
    this.clearPostLoginSystemChatIntent();
    this.clearPendingVerifiedPhone();
  }

  updateAuthData(printingService: string, branch: string) {
    localStorage.removeItem("printingService");
    localStorage.removeItem("branch");
    localStorage.setItem("printingService", printingService);
    localStorage.setItem("branch", branch);
  }

  private getAuthData() {
    const token = localStorage.getItem("token");
    const expirationDate = localStorage.getItem("expiration");
    const userId = localStorage.getItem("userId");
    const printingService = localStorage.getItem("printingService");
    const branch = localStorage.getItem("branch");
    const language = localStorage.getItem("language");
    const roles = JSON.parse(localStorage.getItem("roles"));
    const userName = localStorage.getItem("userName");
    const email = localStorage.getItem("email");
    const invalidToken = !token || token === '' || token === 'undefined' || token === 'null';
    if (invalidToken || !expirationDate) {
      return false;
    }
    return {
      token: token,
      expirationDate: new Date(expirationDate),
      userId: userId,
      printingService: printingService,
      branch: branch,
      language: language,
      roles: roles,
      userName: userName,
      email: email,
    }
  }

  getUser(): any {
    return this.user;
  }

  updateUser(user: any) {
    this.user = user;
    this.userUpdated.next(this.user);
  }

  onSendForgotPasswordEmail(email: string): Promise<any> {
    const url = `${BACKEND_URL}/forgotpassword/${email}`;
    return this.http.post<{}>(url, {}).toPromise();
  }

  onSubmitPassResetCode(email: string, code: string): Promise<any> {
    const url = `${BACKEND_URL}/checkresetpasswordcode/${email}/${code}`;
    return this.http.post<{}>(url, {}).toPromise();
  }

  onResetPassword(email: string, token: string, password: string): Promise<any> {
    const url = `${BACKEND_URL}/resetpassword/${email}`;
    return this.http.post<{}>(url, { token: token, password: password }).toPromise();
  }
}
